import { useState, memo } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Star as OwnerIcon,
  Security as ManagerIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PersonOff as OfflinePlayerIcon,
  Person as PersonIcon,
  GroupAdd as BulkAddIcon,
  GridOn as AssignIcon,
  People as ConnectionsIcon,
} from '@mui/icons-material';
import { IconPicker, renderIcon } from '../IconPicker';
import { BulkImportPlayersDialog, ConfirmDialog } from '../shared';
import { offlinePlayersApi, squareRequestsApi } from '../../api';
import type { User, OfflinePlayer, BoardOfflinePlayer } from '../../types';
import { usePlayersStore } from '../../stores';

export interface PlayerInfo {
  userId: string;
  user?: User;
  role: 'owner' | 'manager' | 'player';
  joinedAt?: string;
}

export interface PlayersPanelProps {
  players: PlayerInfo[];
  offlinePlayers: OfflinePlayer[];
  isManager: boolean;
  isOwner: boolean;
  boardId: string;
  onPromoteToManager: (userId: string) => Promise<void>;
  onRemoveManager: (userId: string) => Promise<void>;
  onOfflinePlayersChange: () => void;
  onSquaresAssigned?: () => void;
  onOpenAddConnectionDialog?: () => void;
  squaresPerCell?: number; // 1 for standard (default), 2 for paired, 4 for quad
}

// Helper to get initials from a name
const getInitials = (name?: string): string => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Role icon component
const getRoleIcon = (role: 'owner' | 'manager' | 'player') => {
  if (role === 'owner') {
    return (
      <Tooltip title="Owner">
        <OwnerIcon sx={{ position: 'absolute', top: -4, right: -4, fontSize: 16, color: 'gold' }} />
      </Tooltip>
    );
  }
  if (role === 'manager') {
    return (
      <Tooltip title="Manager">
        <ManagerIcon sx={{ position: 'absolute', top: -4, right: -4, fontSize: 16, color: 'primary.main' }} />
      </Tooltip>
    );
  }
  return null;
};

// Memoized player avatar component
interface PlayerAvatarProps {
  player: PlayerInfo;
  isManager: boolean;
  isOwner: boolean;
  onOpenPromoteConfirm: (userId: string, displayName: string) => void;
  onOpenRemoveConfirm: (userId: string, displayName: string) => void;
  onOpenAssignSquares: (userId: string, displayName: string) => void;
}

const PlayerAvatar = memo<PlayerAvatarProps>(({ player, isManager, isOwner, onOpenPromoteConfirm, onOpenRemoveConfirm, onOpenAssignSquares }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
    <Box sx={{ position: 'relative' }}>
      <Avatar
        variant="rounded"
        sx={{
          width: 56,
          height: 56,
          bgcolor: player.role === 'owner' ? 'warning.main' : player.role === 'manager' ? 'primary.main' : 'grey.400',
          borderRadius: 2,
        }}
      >
        {getInitials(player.user?.displayName)}
      </Avatar>
      {getRoleIcon(player.role)}
    </Box>
    <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {player.user?.displayName || 'Unknown'}
    </Typography>
    {isManager && player.role === 'player' && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Button
          size="small"
          variant="text"
          startIcon={<AssignIcon sx={{ fontSize: 14 }} />}
          onClick={() => onOpenAssignSquares(player.userId, player.user?.displayName || 'Unknown')}
          sx={{ fontSize: '0.7rem', py: 0 }}
        >
          Assign Squares
        </Button>
        <Button
          size="small"
          variant="text"
          onClick={() => onOpenPromoteConfirm(player.userId, player.user?.displayName || 'Unknown')}
          sx={{ fontSize: '0.7rem', py: 0 }}
        >
          Make Manager
        </Button>
      </Box>
    )}
    {isOwner && player.role === 'manager' && (
      <Button
        size="small"
        variant="text"
        color="error"
        onClick={() => onOpenRemoveConfirm(player.userId, player.user?.displayName || 'Unknown')}
        sx={{ fontSize: '0.7rem', py: 0 }}
      >
        Remove Manager
      </Button>
    )}
  </Box>
));

PlayerAvatar.displayName = 'PlayerAvatar';

// Memoized offline player card
interface OfflinePlayerCardProps {
  player: OfflinePlayer;
  onEdit: (player: OfflinePlayer) => void;
  onRemove: (playerId: string) => void;
  squaresPerCell: number;
}

const OfflinePlayerCard = memo<OfflinePlayerCardProps>(({ player, onEdit, onRemove, squaresPerCell }) => {
  const boardPlayer = player as BoardOfflinePlayer;
  // Convert claimed squares to cells for display (claimedSquares counts actual Square records)
  const claimedCells = Math.round((boardPlayer.claimedSquares || 0) / squaresPerCell);
  // approvedSquares is stored as cells directly (user enters approved cell count)
  const approvedCells = boardPlayer.approvedSquares || 0;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Box sx={{ position: 'relative' }}>
        <Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: 'grey.600', borderRadius: 2 }}>
          {player.iconName ? renderIcon(player.iconName, 28) : getInitials(player.displayName)}
        </Avatar>
        <Tooltip title="Offline Player">
          <OfflinePlayerIcon sx={{ position: 'absolute', top: -4, right: -4, fontSize: 16, color: 'grey.500' }} />
        </Tooltip>
      </Box>
      <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {player.displayName}
      </Typography>
      {player.handle && (
        <Typography variant="caption" color="text.secondary">
          @{player.handle}
        </Typography>
      )}
      <Chip
        size="small"
        label={`${claimedCells}/${approvedCells}`}
        color={claimedCells === approvedCells && approvedCells > 0 ? 'success' : 'default'}
        variant="outlined"
        sx={{ fontSize: '0.7rem', height: 20 }}
      />
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <IconButton size="small" onClick={() => onEdit(player)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <Tooltip title="Remove from board">
          <IconButton size="small" onClick={() => onRemove(player.id)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
});

OfflinePlayerCard.displayName = 'OfflinePlayerCard';

export const PlayersPanel = memo<PlayersPanelProps>(({
  players,
  offlinePlayers,
  isManager,
  isOwner,
  boardId,
  onPromoteToManager,
  onRemoveManager,
  onOfflinePlayersChange,
  onSquaresAssigned,
  onOpenAddConnectionDialog,
  squaresPerCell = 1,
}) => {
  // Use global store to refresh players list (pass boardId directly to ensure it's always set)
  const fetchOfflinePlayersToStore = usePlayersStore((state) => state.fetchOfflinePlayers);

  // Always use "Squares" terminology regardless of cell mode
  const unitLabel = 'Squares';
  const unitLabelLower = 'squares';
  const totalCells = squaresPerCell > 1 ? (squaresPerCell === 2 ? 50 : 25) : 100;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<OfflinePlayer | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [handle, setHandle] = useState('');
  const [iconName, setIconName] = useState('');
  const [editApprovedSquares, setEditApprovedSquares] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [newPlayerStartingBalance, setNewPlayerStartingBalance] = useState<number>(0);

  // State for promote/remove manager confirmation dialogs
  const [promoteConfirmOpen, setPromoteConfirmOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [pendingManagerAction, setPendingManagerAction] = useState<{ userId: string; displayName: string } | null>(null);

  // State for assign squares dialog
  const [assignSquaresDialogOpen, setAssignSquaresDialogOpen] = useState(false);
  const [assignSquaresTarget, setAssignSquaresTarget] = useState<{ userId: string; displayName: string } | null>(null);
  const [assignSquaresCount, setAssignSquaresCount] = useState<number>(1);
  const [assigningSquares, setAssigningSquares] = useState(false);
  const [assignSquaresError, setAssignSquaresError] = useState<string | null>(null);

  const handleOpenPromoteConfirm = (userId: string, displayName: string) => {
    setPendingManagerAction({ userId, displayName });
    setPromoteConfirmOpen(true);
  };

  const handleOpenRemoveConfirm = (userId: string, displayName: string) => {
    setPendingManagerAction({ userId, displayName });
    setRemoveConfirmOpen(true);
  };

  const handleConfirmPromote = async () => {
    if (!pendingManagerAction) return;
    try {
      await onPromoteToManager(pendingManagerAction.userId);
    } finally {
      setPromoteConfirmOpen(false);
      setPendingManagerAction(null);
    }
  };

  const handleConfirmRemove = async () => {
    if (!pendingManagerAction) return;
    try {
      await onRemoveManager(pendingManagerAction.userId);
    } finally {
      setRemoveConfirmOpen(false);
      setPendingManagerAction(null);
    }
  };

  // Assign squares to online player handlers
  const handleOpenAssignSquares = (userId: string, displayName: string) => {
    setAssignSquaresTarget({ userId, displayName });
    setAssignSquaresCount(1);
    setAssignSquaresError(null);
    setAssignSquaresDialogOpen(true);
  };

  const handleCloseAssignSquares = () => {
    setAssignSquaresDialogOpen(false);
    setAssignSquaresTarget(null);
    setAssignSquaresCount(1);
    setAssignSquaresError(null);
  };

  const handleConfirmAssignSquares = async () => {
    if (!assignSquaresTarget || assignSquaresCount <= 0) return;
    setAssigningSquares(true);
    setAssignSquaresError(null);
    try {
      await squareRequestsApi.assignSquares(boardId, {
        userId: assignSquaresTarget.userId,
        squares: assignSquaresCount,
      });
      handleCloseAssignSquares();
      onSquaresAssigned?.();
    } catch (err) {
      setAssignSquaresError(err instanceof Error ? err.message : 'Failed to assign squares');
    } finally {
      setAssigningSquares(false);
    }
  };

  const handleOpenDialog = (player?: OfflinePlayer) => {
    if (player) {
      setEditingPlayer(player);
      setDisplayName(player.displayName);
      setHandle(player.handle || '');
      setIconName(player.iconName || '');
      const boardPlayer = player as BoardOfflinePlayer;
      setEditApprovedSquares(boardPlayer.approvedSquares ?? 0);
      // Show existing starting balance for display (read-only after initial creation)
      setNewPlayerStartingBalance(boardPlayer.startingBalance ?? 0);
    } else {
      setEditingPlayer(null);
      setDisplayName('');
      setHandle('');
      setIconName('');
      setEditApprovedSquares(0);
      setNewPlayerStartingBalance(0);
    }
    setError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPlayer(null);
    setDisplayName('');
    setHandle('');
    setIconName('');
    setEditApprovedSquares(0);
    setNewPlayerStartingBalance(0);
    setError(null);
  };

  const handleSave = async () => {
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (editingPlayer) {
        await offlinePlayersApi.updateBoardOfflinePlayer(boardId, editingPlayer.id, {
          displayName: displayName.trim(),
          handle: handle.trim() || undefined,
          iconName: iconName.trim() || undefined,
          approvedSquares: editApprovedSquares,
        });
      } else {
        await offlinePlayersApi.createBoardOfflinePlayer(boardId, {
          displayName: displayName.trim(),
          handle: handle.trim() || undefined,
          iconName: iconName.trim() || undefined,
          approvedSquares: editApprovedSquares,
          startingBalance: newPlayerStartingBalance !== 0 ? newPlayerStartingBalance : undefined,
        });
      }
      handleCloseDialog();
      // Refresh global store and notify parent
      fetchOfflinePlayersToStore(boardId);
      onOfflinePlayersChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save offline player');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveFromBoard = async (playerId: string) => {
    if (!confirm('Remove this player from the board? Any squares assigned to them will be cleared. The player will remain in your profile.')) {
      return;
    }

    try {
      await offlinePlayersApi.removeFromBoard(boardId, playerId);
      // Refresh global store and notify parent
      fetchOfflinePlayersToStore(boardId);
      onOfflinePlayersChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove player from board');
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Players ({players.length})
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {players.map((player) => (
          <PlayerAvatar
            key={player.userId}
            player={player}
            isManager={isManager}
            isOwner={isOwner}
            onOpenPromoteConfirm={handleOpenPromoteConfirm}
            onOpenRemoveConfirm={handleOpenRemoveConfirm}
            onOpenAssignSquares={handleOpenAssignSquares}
          />
        ))}
      </Box>

      {/* Offline Players Section - Manager Only */}
      {isManager && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6">
              Offline Players ({offlinePlayers.length})
            </Typography>
            {onOpenAddConnectionDialog && (
              <Button
                size="small"
                variant="contained"
                startIcon={<ConnectionsIcon />}
                onClick={onOpenAddConnectionDialog}
              >
                Add Player
              </Button>
            )}
            <Button
              size="small"
              variant="text"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Create Offline
            </Button>
            <Button
              size="small"
              variant="text"
              startIcon={<BulkAddIcon />}
              onClick={() => setBulkImportOpen(true)}
            >
              Bulk Import
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Offline players are managed by you and don&apos;t have accounts. You can assign squares to them in the grid.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {offlinePlayers.map((player) => (
              <OfflinePlayerCard
                key={player.id}
                player={player}
                onEdit={handleOpenDialog}
                onRemove={handleRemoveFromBoard}
                squaresPerCell={squaresPerCell}
              />
            ))}
            {offlinePlayers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No offline players yet. Use Add Player to add existing ones or Create Offline.
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <OwnerIcon sx={{ fontSize: 14, color: 'gold', mr: 0.5 }} /> Owner
          </Box>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <ManagerIcon sx={{ fontSize: 14, color: 'primary.main', mr: 0.5 }} /> Manager
          </Box>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <PersonIcon sx={{ fontSize: 14, color: 'grey.500', mr: 0.5 }} /> Player
          </Box>
          {isManager && (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <OfflinePlayerIcon sx={{ fontSize: 14, color: 'grey.500', mr: 0.5 }} /> Offline Player
            </Box>
          )}
        </Typography>
      </Box>

      {/* Add/Edit Offline Player Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editingPlayer ? 'Edit Offline Player' : 'Create Offline Player'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={saving}
            sx={{ mt: 1, mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Handle (4 chars max)"
            value={handle}
            onChange={(e) => setHandle(e.target.value.toUpperCase().slice(0, 4))}
            disabled={saving}
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 4 }}
            helperText="Short identifier shown on squares (e.g., JOHN)"
          />
          <IconPicker
            value={iconName}
            onChange={setIconName}
            disabled={saving}
            label="Icon"
            helperText="Optional icon shown on squares (overrides handle)"
          />
          <TextField
            fullWidth
            type="number"
            label={`Approved ${unitLabel}`}
            value={editApprovedSquares}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              setEditApprovedSquares(isNaN(val) ? 0 : Math.min(totalCells, Math.max(0, val)));
            }}
            disabled={saving}
            sx={{ mt: 2 }}
            inputProps={{ min: 0, max: totalCells }}
            helperText={`How many ${unitLabelLower} this player is allowed on this board (max ${totalCells})`}
          />
          <TextField
            fullWidth
            type="number"
            label="Starting Balance"
            value={newPlayerStartingBalance}
            onChange={(e) => {
              const val = parseFloat(e.target.value);
              setNewPlayerStartingBalance(isNaN(val) ? 0 : val);
            }}
            disabled={saving || !!editingPlayer}
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
              readOnly: !!editingPlayer,
            }}
            helperText={editingPlayer
              ? "Starting balance was set when player was added (view only)"
              : "For players migrating from outside the system. Positive = they've paid, Negative = they owe"
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !displayName.trim()}>
            {saving ? 'Saving...' : editingPlayer ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Import Dialog */}
      <BulkImportPlayersDialog
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onComplete={() => {
          setBulkImportOpen(false);
          onOfflinePlayersChange();
        }}
      />

      {/* Promote to Manager Confirmation Dialog */}
      <ConfirmDialog
        open={promoteConfirmOpen}
        title="Make Manager"
        message={`Are you sure you want to make ${pendingManagerAction?.displayName || 'this player'} a manager? Managers can edit the board, record scores, and manage squares.`}
        confirmLabel="Make Manager"
        confirmColor="primary"
        onConfirm={handleConfirmPromote}
        onCancel={() => {
          setPromoteConfirmOpen(false);
          setPendingManagerAction(null);
        }}
      />

      {/* Remove Manager Confirmation Dialog */}
      <ConfirmDialog
        open={removeConfirmOpen}
        title="Remove Manager"
        icon="warning"
        message={`Are you sure you want to remove ${pendingManagerAction?.displayName || 'this player'} as a manager? They will no longer be able to edit the board or record scores.`}
        confirmLabel="Remove Manager"
        confirmColor="error"
        onConfirm={handleConfirmRemove}
        onCancel={() => {
          setRemoveConfirmOpen(false);
          setPendingManagerAction(null);
        }}
      />

      {/* Assign Squares to Online Player Dialog */}
      <Dialog open={assignSquaresDialogOpen} onClose={handleCloseAssignSquares} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Squares</DialogTitle>
        <DialogContent>
          {assignSquaresError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {assignSquaresError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Assign squares to <strong>{assignSquaresTarget?.displayName}</strong>. This creates an auto-approved request, allowing them to claim the specified number of squares.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label="Number of Squares"
            value={assignSquaresCount}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              setAssignSquaresCount(isNaN(val) ? 1 : Math.min(100, Math.max(1, val)));
            }}
            disabled={assigningSquares}
            inputProps={{ min: 1, max: 100 }}
            helperText="How many squares to assign to this player (1-100)"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignSquares} disabled={assigningSquares}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAssignSquares}
            variant="contained"
            disabled={assigningSquares || assignSquaresCount <= 0}
            startIcon={assigningSquares ? <CircularProgress size={16} /> : <AssignIcon />}
          >
            {assigningSquares ? 'Assigning...' : 'Assign Squares'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

PlayersPanel.displayName = 'PlayersPanel';

export default PlayersPanel;
